import { useMemo, useState } from 'react'
import type { Andar, Equipe, Pessoa, Sala } from '../../pages/OfficePage.tsx'
import { useChamada } from './chamada.tsx'
import { Foto } from './ReuniaoDaSala.tsx'
import './escritorio-celular.css'

/**
 * O Escritório no celular (SPEC-20261011-0330, fotos: SPEC-20261011-0349-fotos-prontas-no-celular): o tabuleiro inteiro numa tela de
 * 6 polegadas fica pequeno demais, então a tela principal é a lista de quem está
 * onde. Um toque na sala entra na chamada; o olho (ou segurar o dedo) abre o
 * cartão sem entrar; o tabuleiro continua no botão "Mapa do andar". A chamada em
 * andamento vira uma bolha que leva à reunião.
 */

type Props = {
  salas: Sala[]
  todasAsSalas: Sala[]
  andares: Andar[]
  andarAtual: string | null
  equipes: Equipe[]
  minhaSala: string | null
  mapa: boolean
  folha: Sala | null
  fotoDe: (p: Pessoa) => string | null
  onFolha: (s: Sala | null) => void
  onAndar: (id: string) => void
  onEntrar: (s: Sala) => void
  onAbrirMapa: () => void
  onAbrirReuniao: () => void
}

const semAcento = (t: string) => t.toLowerCase().normalize('NFD').replace(/\p{Diacritic}/gu, '')
/** A imagem pronta da sala (o cenário realista da reunião), a mesma em todos os modelos e na área central. */
export const fotoDoModelo = (template: string) => `/escritorio/realista/fundos/${template}.jpg?v=1`
export const fotoDaSala = (s: Sala) => fotoDoModelo(s.template)

function Rostos({ gente, fotoDe, max = 4, tamanho = 30 }: { gente: Pessoa[]; fotoDe: Props['fotoDe']; max?: number; tamanho?: number }) {
  return (
    <span className="escc-rostos">
      {gente.slice(0, max).map((p, i) => (
        <span key={p.id} className={`escc-rosto${i === 0 ? ' escc-rosto--fala' : ''}`}>
          <Foto nome={p.name} foto={fotoDe(p)} tamanho={tamanho} />
        </span>
      ))}
      {gente.length > max ? <em className="escc-mais">+{gente.length - max}</em> : null}
    </span>
  )
}

export function EscritorioCelular(p: Props) {
  const ch = useChamada()
  const [busca, setBusca] = useState('')
  const equipeDe = useMemo(() => new Map(p.equipes.map((e) => [e.id, e])), [p.equipes])
  // "sua equipe": as equipes de um grupo em que a pessoa está (as abertas a todos não contam)
  const minhas = useMemo(() => new Set(p.equipes.filter((e) => e.accessGroupId && e.podeEntrar).map((e) => e.id)), [p.equipes])
  const porAndar = useMemo(() => {
    const m = new Map<string, number>()
    for (const s of p.todasAsSalas) m.set(s.floorId, (m.get(s.floorId) ?? 0) + s.pessoas.length)
    return m
  }, [p.todasAsSalas])
  const total = p.andarAtual ? (porAndar.get(p.andarAtual) ?? 0) : 0
  const nomeDoAndar = (id: string) => p.andares.find((a) => a.id === id)?.name ?? ''

  const termo = semAcento(busca.trim())
  const comGente = p.salas.filter((s) => s.pessoas.length)
  const daEquipe = comGente.filter((s) => s.teamId && minhas.has(s.teamId))
  const outras = comGente.filter((s) => !(s.teamId && minhas.has(s.teamId))).sort((a, b) => b.pessoas.length - a.pessoas.length)
  const livres = p.salas.filter((s) => !s.pessoas.length)

  const tocar = (s: Sala) => {
    if (s.id === p.minhaSala) p.onAbrirReuniao()
    else if (s.podeEntrar) p.onEntrar(s)
    else p.onFolha(s)
  }
  const segurar = (s: Sala) => {
    let t = 0
    let segurou = false
    return {
      onPointerDown: () => {
        segurou = false
        t = window.setTimeout(() => {
          segurou = true
          p.onFolha(s)
        }, 480)
      },
      onPointerUp: () => window.clearTimeout(t),
      onPointerCancel: () => window.clearTimeout(t),
      onPointerMove: (e: React.PointerEvent) => {
        if (Math.abs(e.movementY) > 3) window.clearTimeout(t)
      },
      onClickCapture: (e: React.MouseEvent) => {
        if (segurou) {
          e.stopPropagation()
          e.preventDefault()
        }
      },
    }
  }

  const cartao = (s: Sala) => {
    const eq = s.teamId ? equipeDe.get(s.teamId) : undefined
    const estou = s.id === p.minhaSala
    return (
      <div
        key={s.id}
        role="button"
        tabIndex={0}
        className={`escc-sala${s.podeEntrar ? '' : ' escc-sala--trancada'}${eq ? ' escc-sala--equipe' : ''}`}
        style={eq ? { ['--eq' as string]: eq.color } : undefined}
        aria-label={`${s.name}: ${estou ? 'abrir a reunião' : s.podeEntrar ? 'entrar na chamada' : 'trancada'}`}
        onClick={() => tocar(s)}
        onKeyDown={(e) => e.key === 'Enter' && tocar(s)}
        {...segurar(s)}
      >
        <div className="escc-foto" style={{ backgroundImage: `url(${fotoDaSala(s)})` }}>
          {eq ? (
            <span className="escc-equipe" style={{ background: eq.color }}>
              <i className="fas fa-users" aria-hidden /> {eq.name}
            </span>
          ) : null}
          {s.accessGroupId ? (
            <span className="escc-cadeado" aria-hidden>
              <i className="fas fa-lock" />
            </span>
          ) : null}
          <div className="escc-nome">
            <b>{s.name}</b>
            <span>
              <i className="escc-vivo" aria-hidden />
              {estou ? 'Você está aqui' : `${s.pessoas.length} em chamada`}
            </span>
          </div>
          <button
            type="button"
            className="escc-ver"
            aria-label={`Ver ${s.name} sem entrar`}
            onClick={(e) => {
              e.stopPropagation()
              p.onFolha(s)
            }}
          >
            <i className="fas fa-eye" aria-hidden />
          </button>
        </div>
        <div className="escc-pe">
          <Rostos gente={s.pessoas} fotoDe={p.fotoDe} />
          <span className="escc-quem">{s.pessoas.map((x) => x.name.split(' ')[0]).join(', ')}</span>
          <span className="escc-entrar">
            {estou ? (
              <>
                <i className="fas fa-expand-alt" aria-hidden /> Abrir
              </>
            ) : s.podeEntrar ? (
              <>
                <i className="fas fa-phone-alt" aria-hidden /> Entrar
              </>
            ) : (
              <>
                <i className="fas fa-lock" aria-hidden /> Trancada
              </>
            )}
          </span>
        </div>
      </div>
    )
  }

  const achados = termo
    ? {
        pessoas: p.todasAsSalas.flatMap((s) => s.pessoas.filter((x) => semAcento(x.name).includes(termo)).map((x) => ({ x, s }))),
        salas: p.todasAsSalas.filter((s) => semAcento(s.name).includes(termo)),
      }
    : null
  const irAte = (s: Sala) => {
    setBusca('')
    if (s.floorId !== p.andarAtual) p.onAndar(s.floorId)
    p.onFolha(s)
  }

  return (
    <>
      {p.mapa ? null : (
        <div className="escc">
          <div className="escc-topo">
            <p className="escc-agora">
              <i className="escc-pulso" aria-hidden />
              <span>
                <b>{total === 1 ? '1 pessoa' : `${total} pessoas`}</b> agora {p.andares.length > 1 ? 'neste andar' : 'no escritório'}
              </span>
            </p>
            {p.andares.length > 1 ? (
              <div className="escc-andares" role="tablist" aria-label="Andares">
                {p.andares.map((a) => (
                  <button key={a.id} type="button" role="tab" aria-selected={a.id === p.andarAtual} className={`escc-andar${a.id === p.andarAtual ? ' escc-andar--on' : ''}`} onClick={() => p.onAndar(a.id)}>
                    <i className="fas fa-building" aria-hidden /> {a.name}
                    <em>{porAndar.get(a.id) ?? 0}</em>
                  </button>
                ))}
              </div>
            ) : null}
            <label className="escc-busca">
              <i className="fas fa-search" aria-hidden />
              <input type="search" value={busca} onChange={(e) => setBusca(e.target.value)} placeholder="Procurar pessoa ou sala" aria-label="Procurar pessoa ou sala" />
            </label>
          </div>

          {achados ? (
            <div className="escc-secao">
              {achados.pessoas.length ? <h2>Pessoas</h2> : null}
              {achados.pessoas.map(({ x, s }) => (
                <div key={x.id} className="escc-achado">
                  <Foto nome={x.name} foto={p.fotoDe(x)} tamanho={40} />
                  <span className="escc-achado-txt">
                    <b>{x.name}</b>
                    <small>
                      está em {s.name}
                      {s.floorId !== p.andarAtual ? ` · ${nomeDoAndar(s.floorId)}` : ''}
                    </small>
                  </span>
                  <button type="button" className="escc-ir" onClick={() => irAte(s)}>
                    Ir até lá
                  </button>
                </div>
              ))}
              {achados.salas.length ? <h2>Salas</h2> : null}
              {achados.salas.map((s) => (
                <button key={s.id} type="button" className="escc-achado" onClick={() => irAte(s)}>
                  <span className="escc-mini" style={{ backgroundImage: `url(${fotoDaSala(s)})` }} />
                  <span className="escc-achado-txt">
                    <b>{s.name}</b>
                    <small>
                      {nomeDoAndar(s.floorId)} · {s.pessoas.length ? `${s.pessoas.length} em chamada` : 'livre'}
                    </small>
                  </span>
                  <i className="fas fa-chevron-right" aria-hidden />
                </button>
              ))}
              {!achados.pessoas.length && !achados.salas.length ? <p className="escc-vazio">Ninguém com esse nome está numa sala agora.</p> : null}
            </div>
          ) : (
            <>
              {daEquipe.length ? (
                <div className="escc-secao">
                  <h2>
                    <i className="escc-bolinha" style={{ background: equipeDe.get(daEquipe[0]?.teamId ?? '')?.color }} />
                    Sua equipe agora
                  </h2>
                  {daEquipe.map(cartao)}
                </div>
              ) : null}
              {outras.length ? (
                <div className="escc-secao">
                  <h2>
                    <i className="escc-bolinha escc-bolinha--verde" />
                    Com gente
                  </h2>
                  {outras.map(cartao)}
                </div>
              ) : null}
              {!comGente.length ? <p className="escc-vazio">Ninguém numa sala agora. Toque numa sala livre para abrir: quem chegar depois entra com você.</p> : null}
              {livres.length ? (
                <div className="escc-secao">
                  <h2>Salas livres · toque para abrir</h2>
                  <div className="escc-livres">
                    {livres.map((s) => {
                      const eq = s.teamId ? equipeDe.get(s.teamId) : undefined
                      return (
                        <button key={s.id} type="button" className="escc-livre" onClick={() => tocar(s)} {...segurar(s)}>
                          <span className="escc-livre-foto" style={{ backgroundImage: `url(${fotoDaSala(s)})` }}>
                            {s.accessGroupId ? (
                              <span className="escc-cadeado escc-cadeado--p" aria-hidden>
                                <i className="fas fa-lock" />
                              </span>
                            ) : null}
                          </span>
                          <span className="escc-livre-txt">
                            <b>{s.name}</b>
                            <small>
                              <i style={{ background: eq?.color }} />
                              {eq ? eq.name : 'Livre'}
                            </small>
                          </span>
                        </button>
                      )
                    })}
                  </div>
                </div>
              ) : null}
            </>
          )}
          <button type="button" className="escc-mapa" onClick={p.onAbrirMapa}>
            <i className="fas fa-map" aria-hidden /> Mapa do andar
          </button>
        </div>
      )}

      {ch?.sala ? (
        <div className={`escc-bolha${p.mapa ? ' escc-bolha--mapa' : ''}`} role="button" tabIndex={0} aria-label={`Voltar para a reunião em ${ch.sala.nome}`} onClick={p.onAbrirReuniao} onKeyDown={(e) => e.key === 'Enter' && p.onAbrirReuniao()}>
          <span className="escc-bolha-foto" style={{ backgroundImage: `url(${fotoDoModelo(ch.sala.template)})` }}>
            {ch.quadros[1] ? <Foto nome={ch.quadros[1].nome} foto={ch.quadros[1].foto} tamanho={34} /> : <Foto nome={ch.quadros[0]?.nome ?? 'Você'} foto={ch.quadros[0]?.foto ?? null} tamanho={34} />}
            <span className="escc-onda" aria-hidden>
              <i />
              <i />
              <i />
              <i />
            </span>
          </span>
          <span className="escc-bolha-pe">
            <span className="escc-bolha-txt">
              <b>{ch.sala.nome}</b>
              <small>
                {ch.quadros.length} na chamada{ch.micLigado ? '' : ' · mudo'}
              </small>
            </span>
            <button
              type="button"
              className={`escc-bolha-mic${ch.micLigado ? '' : ' escc-bolha-mic--off'}`}
              aria-label={ch.micLigado ? 'Desligar microfone' : 'Ligar microfone'}
              onClick={(e) => {
                e.stopPropagation()
                ch.alternarMic()
              }}
            >
              <i className={`fas ${ch.micLigado ? 'fa-microphone' : 'fa-microphone-slash'}`} aria-hidden />
            </button>
            <button
              type="button"
              className="escc-bolha-sair"
              aria-label="Sair da chamada"
              onClick={(e) => {
                e.stopPropagation()
                ch.sair()
              }}
            >
              <i className="fas fa-phone-slash" aria-hidden />
            </button>
          </span>
        </div>
      ) : null}

      <div className={`escc-veu${p.folha ? ' escc-veu--on' : ''}`} onClick={() => p.onFolha(null)} aria-hidden />
      <div className={`escc-folha${p.folha ? ' escc-folha--on' : ''}`} role="dialog" aria-label={p.folha?.name ?? 'Sala'} aria-hidden={!p.folha}>
        {p.folha ? <FolhaDaSala s={p.folha} equipe={p.folha.teamId ? equipeDe.get(p.folha.teamId) : undefined} andar={nomeDoAndar(p.folha.floorId)} estou={p.folha.id === p.minhaSala} fotoDe={p.fotoDe} onEntrar={() => tocar(p.folha as Sala)} /> : null}
      </div>
    </>
  )
}

function FolhaDaSala({ s, equipe, andar, estou, fotoDe, onEntrar }: { s: Sala; equipe: Equipe | undefined; andar: string; estou: boolean; fotoDe: Props['fotoDe']; onEntrar: () => void }) {
  return (
    <>
      <span className="escc-puxador" aria-hidden />
      <div className="escc-folha-foto" style={{ backgroundImage: `url(${fotoDaSala(s)})` }} />
      <div className="escc-folha-corpo">
        <h3>{s.name}</h3>
        <p className="escc-folha-sub">
          {andar}
          {' · '}
          {s.pessoas.length ? `${s.pessoas.length} em chamada agora` : 'Ninguém aqui ainda'}
          {equipe ? ` · Equipe ${equipe.name}` : ''}
        </p>
        {s.pessoas.length ? (
          <ul className="escc-gente">
            {s.pessoas.map((x) => (
              <li key={x.id}>
                <Foto nome={x.name} foto={fotoDe(x)} tamanho={36} />
                <span>
                  {x.name}
                  {x.bio ? <small>{x.bio}</small> : null}
                </span>
              </li>
            ))}
          </ul>
        ) : (
          <div className="escc-lugares" aria-hidden>
            <i />
            <i />
            <i />
            <i />
          </div>
        )}
        {s.podeEntrar || estou ? (
          <button type="button" className="escc-botao" onClick={onEntrar}>
            <i className={`fas ${estou ? 'fa-expand-alt' : 'fa-phone-alt'}`} aria-hidden />
            {estou ? 'Abrir a reunião' : s.pessoas.length ? 'Entrar na chamada' : 'Abrir a sala'}
          </button>
        ) : (
          <p className="escc-trancada">
            <i className="fas fa-lock" aria-hidden />
            <span>
              Sala trancada. Só quem é de {s.accessGroupName ?? 'um grupo'} entra. Você vê quem está, mas não entra.
            </span>
          </p>
        )}
      </div>
    </>
  )
}
